if (!customElements.get('swiper-carousel')) {
  class SwiperCarousel extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector('.swiper');
      this.handleChange = this.handleChange.bind(this);

      const activeMaxWidth = this.dataset.activeMaxWidth;
      if (activeMaxWidth) {
        this.mediaQuery = window.matchMedia(`(max-width: ${activeMaxWidth}px)`);
        this.mediaQuery.addEventListener('change', this.handleChange);
        this.handleChange(this.mediaQuery);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this.mediaQuery) {
        this.mediaQuery.removeEventListener('change', this.handleChange);
      }
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
      this.destroy();
    }

    handleChange(event) {
      if (event.matches) {
        this.init();
      } else {
        this.destroy();
      }
    }

    init(attempt) {
      attempt = attempt || 0;

      if (this.swiper || !this.swiperEl) return;

      // The global Swiper bundle is a separate deferred script; on first page
      // load it wins the race by source order, but theme-editor section
      // reloads and multi-section pages can call this before it's ready.
      // Retry instead of silently giving up.
      if (typeof Swiper === 'undefined') {
        if (attempt > 100) {
          console.error('swiper-carousel: Swiper library never became available');
          return;
        }
        this.retryTimer = setTimeout(() => this.init(attempt + 1), 50);
        return;
      }

      const options = {
        slidesPerView: Number(this.dataset.slidesPerView) || 1,
        spaceBetween: Number(this.dataset.spaceBetween) || 0,
        loop: this.dataset.loop === 'true',
        // Mouse-drag scrolling (touch already works via native browser
        // behavior). simulateTouch is Swiper's default, set explicitly so
        // drag-to-scroll can't be silently lost if defaults ever change.
        simulateTouch: this.dataset.simulateTouch !== 'false',
        grabCursor: this.dataset.grabCursor !== 'false',
      };

      const autoplayDelay = Number(this.dataset.autoplayDelay);
      if (autoplayDelay > 0) {
        options.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
        };
      }

      if (this.dataset.breakpoints) {
        try {
          options.breakpoints = JSON.parse(this.dataset.breakpoints);
        } catch (error) {
          console.error('swiper-carousel: invalid data-breakpoints JSON', error);
        }
      }

      // Searched on the whole component (not just .swiper) so nav buttons
      // and pagination can live anywhere inside <swiper-carousel> — e.g. a
      // prev/next pair placed in a header row above the .swiper track.
      const pagination = this.querySelector('.swiper-pagination');
      if (pagination) {
        options.pagination =
          this.dataset.paginationType === 'progressbar'
            ? { el: pagination, type: 'progressbar' }
            : { el: pagination, clickable: true };
      }

      const nextEl = this.querySelector('.swiper-button-next');
      const prevEl = this.querySelector('.swiper-button-prev');
      if (nextEl || prevEl) {
        options.navigation = { nextEl, prevEl };
      }

      this.swiper = new Swiper(this.swiperEl, options);
    }

    destroy() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define('swiper-carousel', SwiperCarousel);
}
